import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { forwardRef, useImperativeHandle } from 'react';
import { Edit, Trash } from '../../../icons';
import { BottomSheetContentLayout } from '../../BottomSheet';
import { Card } from '../../Card';
import { Divider } from '../../Divider';
import { IconButton } from '../../IconButton';
import { Column, Row } from '../../Layout';
import { useOverlayContext } from '../../OverlayContext';
import { Typography } from '../../Typography';
import styles from '../EditableTableList.module.scss';
import { buttonBi, isEmptyValue } from '../EditableTableList.utils';
import { SortableCard } from '../SortableCard';
function EditableTableListMobileInner(param, ref) {
    let { rows, columns, getRowId, onRemoveRow, renderRowFooter, editSheetHeader, renderEditSheetFooter, renderEditSheet, bi, testIds, dnd } = param;
    const { dragEnabled, rowIds, rowIsStatic, withDndContext, withSortableItems } = dnd;
    const { pushBottomSheetPage, popBottomSheetPage } = useOverlayContext();
    const primaryColumn = columns.find((column)=>column.isPrimary);
    const fieldColumns = columns.filter((column)=>!column.isPrimary);
    const editableColumns = columns.filter((column)=>!column.isPrimary && column.renderEdit != null);
    const renderPrimaryTitle = (row, rowIndex)=>{
        var _ref;
        var _this;
        return (_this = (_ref = primaryColumn === null || primaryColumn === void 0 ? void 0 : primaryColumn.renderSummary) !== null && _ref !== void 0 ? _ref : primaryColumn === null || primaryColumn === void 0 ? void 0 : primaryColumn.renderCell) === null || _this === void 0 ? void 0 : _this(row, rowIndex);
    };
    const openEditSheet = function openEditSheet(row, rowIndex) {
        let isAutoOpen = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false;
        var _ref;
        if (renderEditSheet != null) {
            pushBottomSheetPage({
                content: renderEditSheet(row, rowIndex, {
                    close: popBottomSheetPage,
                    isAutoOpen
                })
            });
            return;
        }
        pushBottomSheetPage({
            content: /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
                contentGutter: "m",
                header: _object_spread({
                    gutter: 'm'
                }, (_ref = editSheetHeader === null || editSheetHeader === void 0 ? void 0 : editSheetHeader(row, rowIndex)) !== null && _ref !== void 0 ? _ref : {
                    title: /*#__PURE__*/ _jsx(_Fragment, {
                        children: renderPrimaryTitle(row, rowIndex)
                    })
                }),
                footer: renderEditSheetFooter != null ? {
                    content: renderEditSheetFooter(row, rowIndex, {
                        close: popBottomSheetPage
                    })
                } : undefined,
                children: /*#__PURE__*/ _jsx(Column, {
                    gap: 16,
                    className: styles.editSheetContent,
                    children: editableColumns.map((column)=>{
                        var _column_renderEdit;
                        return /*#__PURE__*/ _jsx("div", {
                            "data-testid": testIds.EditField(rowIndex, column.key),
                            children: (_column_renderEdit = column.renderEdit) === null || _column_renderEdit === void 0 ? void 0 : _column_renderEdit.call(column, row, rowIndex)
                        }, column.key);
                    })
                })
            })
        });
    };
    useImperativeHandle(ref, ()=>({
            openEditRow: (rowId)=>{
                const rowIndex = rows.findIndex((row, index)=>getRowId(row, index) === rowId);
                if (rowIndex !== -1) openEditSheet(rows[rowIndex], rowIndex, true);
            }
        }));
    const cardEditableProps = (row, rowIndex)=>rowIsStatic(row, rowIndex) ? {} : {
            onClick: ()=>openEditSheet(row, rowIndex)
        };
    const renderCardContent = (row, rowIndex)=>/*#__PURE__*/ _jsxs(Card, _object_spread_props(_object_spread({
            noBi: true,
            testId: testIds.Card(rowIndex),
            padding: "m",
            borderRadius: "m"
        }, cardEditableProps(row, rowIndex)), {
            children: [
                /*#__PURE__*/ _jsxs(Row, {
                    justifyContent: "space-between",
                    alignItems: "center",
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            weight: "bold",
                            testId: testIds.CardTitle(rowIndex),
                            children: renderPrimaryTitle(row, rowIndex)
                        }),
                        !rowIsStatic(row, rowIndex) && /*#__PURE__*/ _jsxs(Row, {
                            gap: 8,
                            onClick: (event)=>event.stopPropagation(),
                            onPointerDown: (event)=>event.stopPropagation(),
                            children: [
                                /*#__PURE__*/ _jsx(IconButton, _object_spread_props(_object_spread({}, buttonBi(bi === null || bi === void 0 ? void 0 : bi.editButton)), {
                                    size: "small",
                                    testId: testIds.EditButton(rowIndex),
                                    onClick: ()=>openEditSheet(row, rowIndex),
                                    children: /*#__PURE__*/ _jsx(Edit, {
                                        size: 16
                                    })
                                })),
                                onRemoveRow != null && /*#__PURE__*/ _jsx(IconButton, _object_spread_props(_object_spread({}, buttonBi(bi === null || bi === void 0 ? void 0 : bi.removeButton)), {
                                    size: "small",
                                    testId: testIds.RemoveButton(rowIndex),
                                    onClick: ()=>onRemoveRow(rowIndex),
                                    children: /*#__PURE__*/ _jsx(Trash, {
                                        size: 16
                                    })
                                }))
                            ]
                        })
                    ]
                }),
                fieldColumns.map((column)=>{
                    var _column_renderSummary;
                    const value = ((_column_renderSummary = column.renderSummary) !== null && _column_renderSummary !== void 0 ? _column_renderSummary : column.renderCell)(row, rowIndex);
                    if (isEmptyValue(value)) return null;
                    return /*#__PURE__*/ _jsxs(React.Fragment, {
                        children: [
                            column.dividerBefore && /*#__PURE__*/ _jsx("div", {
                                "data-testid": testIds.CardFieldDivider(rowIndex, column.key),
                                children: /*#__PURE__*/ _jsx(Divider, {
                                    margin: 8
                                })
                            }),
                            column.stacked ? /*#__PURE__*/ _jsxs(Column, {
                                gap: 4,
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        skin: "secondary",
                                        children: column.header
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        testId: testIds.CardField(rowIndex, column.key),
                                        children: value
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsxs(Row, {
                                justifyContent: "space-between",
                                alignItems: "center",
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        skin: "secondary",
                                        children: column.header
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        testId: testIds.CardField(rowIndex, column.key),
                                        children: value
                                    })
                                ]
                            })
                        ]
                    }, column.key);
                }),
                renderRowFooter != null && /*#__PURE__*/ _jsx("div", {
                    "data-testid": testIds.RowFooter(rowIndex),
                    children: renderRowFooter(row, rowIndex)
                })
            ]
        }));
    return withDndContext(/*#__PURE__*/ _jsx(Column, {
        gap: 12,
        testId: testIds.Root(),
        children: withSortableItems(rows.map((row, rowIndex)=>dragEnabled && !rowIsStatic(row, rowIndex) ? /*#__PURE__*/ _jsx(SortableCard, {
                id: rowIds[rowIndex],
                children: renderCardContent(row, rowIndex)
            }, rowIds[rowIndex]) : /*#__PURE__*/ _jsx(React.Fragment, {
                children: renderCardContent(row, rowIndex)
            }, rowIds[rowIndex])))
    }));
}
export const EditableTableListMobile = /*#__PURE__*/ forwardRef(EditableTableListMobileInner);
